import { z } from 'zod'
import { manufacturingApprovalSchema, manufacturingPublishSchema } from './manufacturing'

const id = z.string().min(1).max(200)
const text = z.string().trim().min(1).max(12000)
const amount = z.string().regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,4})?$/)
const operation = { operation_id: z.uuid() }
export const manufacturingOperationSchema = z.object(operation).strict()
export const manufacturingFactoryBindingSchema = z
  .object({
    ...operation,
    clerk_user_id: id,
    local_account_id: id,
    entity_id: id,
    active: z.boolean(),
  })
  .strict()
export const manufacturingIntakeConsentSchema = z
  .object({ ...operation, consent: z.literal(true), stage: z.enum(['prototype', 'production']) })
  .strict()
export const manufacturingCancelSchema = z.object({ ...operation, reason: text }).strict()
const offer = { offer_id: id }
const evidence = { evidence: text }

/** No email, payment, AI invocation or arbitrary local route can be queued. */
export const manufacturingOperatorActionSchema = z.discriminatedUnion('action', [
  z.object({ ...operation, action: z.literal('register_account'), entity_id: id }).strict(),
  manufacturingPublishSchema.extend({ action: z.literal('publish_quote') }).strict(),
  z.object({ ...operation, action: z.literal('withdraw_quote'), publication_id: id }).strict(),
  z
    .object({
      ...operation,
      ...offer,
      action: z.literal('review_offer'),
      validity_confirmed: z.boolean().optional(),
    })
    .strict(),
  z.object({ ...operation, ...offer, ...evidence, action: z.literal('approve_order') }).strict(),
  z
    .object({
      ...operation,
      ...offer,
      ...evidence,
      action: z.literal('record_receipt'),
      amount,
      receipt_ref: id,
    })
    .strict(),
  z
    .object({
      ...operation,
      ...offer,
      ...evidence,
      action: z.literal('refund_receipt'),
      amount,
      refund_ref: id,
    })
    .strict(),
  z
    .object({
      ...operation,
      ...offer,
      action: z.literal('calculate_fee'),
      agreement_id: id,
      support_minutes: amount,
      hourly_cost: amount,
      variable_cost: amount,
    })
    .strict(),
  z
    .object({ ...operation, ...evidence, action: z.literal('mark_fee_paid'), billing_id: id })
    .strict(),
  z
    .object({
      ...operation,
      ...evidence,
      action: z.literal('create_agreement'),
      request_id: id,
      entity_id: id,
      rate_percent: amount.refine((v) => Number(v) <= 100),
      terms: text,
      consent: z.literal(true),
    })
    .strict(),
  z
    .object({
      ...operation,
      action: z.literal('assign_request'),
      request_id: id,
      entity_id: id,
      shared_brief: text,
    })
    .strict(),
  z.object({ ...operation, action: z.literal('revoke_assignment'), assignment_id: id }).strict(),
  z
    .object({
      ...operation,
      action: z.literal('set_account_active'),
      account_id: id,
      active: z.boolean(),
    })
    .strict(),
  z
    .object({ ...operation, action: z.literal('cancel_request'), request_id: id, reason: text })
    .strict(),
])
export type ManufacturingOperatorAction = z.infer<typeof manufacturingOperatorActionSchema>

export const manufacturingFactoryActionSchema = z.discriminatedUnion('action', [
  z
    .object({
      ...operation,
      ...evidence,
      action: z.literal('update_order'),
      order_id: id,
      status: z.enum(['in_production', 'ready', 'completed']),
    })
    .strict(),
  z
    .object({
      ...operation,
      action: z.literal('submit_offer'),
      request_id: id,
      proposed_quantity: amount,
      unit: z.string().min(1).max(50),
      stage: z.string().min(1).max(100),
      amount: amount.optional(),
      currency: z.literal('KRW'),
      tax_status: z.enum(['included', 'excluded', 'unknown']),
      price_scope: z.enum(['total', 'unknown']),
      valid_until: z.iso.datetime({ offset: true }).optional(),
      conditions: text,
    })
    .strict(),
  z.object({ ...operation, ...evidence, action: z.literal('accept_order'), order_id: id }).strict(),
])
export type ManufacturingFactoryAction = z.infer<typeof manufacturingFactoryActionSchema>

export const manufacturingIntakeSchema = z
  .object({
    ...operation,
    external_request_id: id,
    product: z.string().min(1).max(300),
    quantity: amount,
    unit: z.string().min(1).max(50),
    unit_weight: z.string().min(1).max(100),
    stage: z.string().min(1).max(100),
  })
  .strict()
export const manufacturingCommandSchema = z.discriminatedUnion('kind', [
  z
    .object({ kind: z.literal('bind_factory_account'), body: manufacturingFactoryBindingSchema })
    .strict(),
  z.object({ kind: z.literal('intake'), body: manufacturingIntakeSchema }).strict(),
  z.object({ kind: z.literal('approve'), body: manufacturingApprovalSchema }).strict(),
  z.object({ kind: z.literal('cancel'), body: manufacturingCancelSchema }).strict(),
  z.object({ kind: z.literal('operator'), body: manufacturingOperatorActionSchema }).strict(),
  z.object({ kind: z.literal('factory'), body: manufacturingFactoryActionSchema }).strict(),
  z.object({ kind: z.literal('refresh'), body: z.object(operation).strict() }).strict(),
])
export type ManufacturingCommand = z.infer<typeof manufacturingCommandSchema>

const customerRequest = z.object({
  id,
  external_request_id: id,
  product: z.string(),
  quantity: z.string(),
  unit: z.string(),
  unit_weight: z.string(),
  stage: z.string(),
  status: z.string(),
  spec_version: z.number().int().positive().optional(),
  created_at: z.string().optional(),
})
const customerQuote = z.object({
  id,
  request_id: id,
  version: z.number().int().positive(),
  status: z.string(),
  product: z.string(),
  requested_quantity: z.string(),
  proposed_quantity: z.string(),
  unit: z.string(),
  unit_weight: z.string(),
  stage: z.string(),
  customer_amount: z.string(),
  currency: z.string(),
  price_basis: z.string(),
  customer_terms: z.string(),
  valid_until: z.string(),
  published_at: z.string(),
  approved_at: z.string().optional(),
  can_approve: z.boolean(),
})
/** Customer projection deliberately has no factory cost, evidence, contact or identity fields. */
export const manufacturingCustomerSnapshotSchema = z.object({
  requests: z.array(customerRequest).max(1),
  quotes: z.array(customerQuote).max(100),
  as_of: z.string().optional(),
  orders: z
    .array(
      z.object({ id, request_id: id, status: z.string(), quantity: z.string(), unit: z.string() }),
    )
    .max(100)
    .default([]),
})

const record = z.object({
  id,
  request_id: id.optional(),
  offer_id: id.optional(),
  order_id: id.optional(),
  entity_id: id.optional(),
  account_id: id.optional(),
  external_request_id: id.optional(),
  factory_name: z.string().optional(),
  product: z.string().optional(),
  quantity: z.string().optional(),
  requested_quantity: z.string().optional(),
  proposed_quantity: z.string().optional(),
  unit: z.string().optional(),
  unit_weight: z.string().optional(),
  stage: z.string().optional(),
  amount: z.string().nullable().optional(),
  currency: z.string().optional(),
  tax_status: z.string().optional(),
  price_scope: z.string().optional(),
  valid_until: z.string().nullable().optional(),
  conditions: z.string().optional(),
  status: z.string().optional(),
  next_status: z.enum(['in_production', 'ready', 'completed']).nullable().optional(),
  source: z.string().optional(),
  spec_version: z.number().int().optional(),
  created_at: z.string().optional(),
  shared_brief: z.string().optional(),
  active: z.boolean().optional(),
  assignment_active: z.boolean().optional(),
  can_submit: z.boolean().optional(),
  can_accept: z.boolean().optional(),
  rate_percent: z.string().optional(),
  eligible_receipts: z.string().optional(),
  fee_amount: z.string().optional(),
  outstanding: z.string().optional(),
  credit_due: z.string().optional(),
  paid_amount: z.string().optional(),
  terms: z.string().optional(),
  consent: z.boolean().optional(),
  customer_amount: z.string().optional(),
  customer_terms: z.string().optional(),
  version: z.number().int().optional(),
  published_at: z.string().optional(),
  approved_at: z.string().optional(),
  receipt_ref: z.string().optional(),
  refund_ref: z.string().optional(),
  gross_receipts: z.string().optional(),
  refunded_amount: z.string().optional(),
  operating_cost: z.string().optional(),
  contribution: z.string().optional(),
})
const factoryRecord = record.omit({
  operating_cost: true,
  contribution: true,
  receipt_ref: true,
  refund_ref: true,
  source: true,
  customer_amount: true,
  customer_terms: true,
  published_at: true,
  approved_at: true,
})
export const manufacturingFactorySnapshotSchema = z.object({
  requests: z.array(factoryRecord).max(1000),
  offers: z.array(factoryRecord).max(1000),
  orders: z.array(factoryRecord).max(1000),
  billing: z.array(factoryRecord).max(1000),
  agreements: z.array(factoryRecord).max(1000),
})
export const manufacturingOperatorSnapshotSchema = z.object({
  receipts: z.array(record).max(5000).default([]),
  refunds: z.array(record).max(5000).default([]),
  requests: z.array(record).max(2000),
  offers: z.array(record).max(5000),
  orders: z.array(record).max(2000),
  billing: z.array(record).max(2000),
  agreements: z.array(record).max(2000),
  publications: z.array(record).max(5000),
  accounts: z
    .array(
      z.object({
        id,
        username: z.string(),
        entity_id: id,
        factory_name: z.string(),
        active: z.boolean(),
      }),
    )
    .max(2000),
  assignments: z
    .array(
      z.object({
        id,
        request_id: id,
        entity_id: id,
        shared_brief: z.string(),
        active: z.union([z.boolean(), z.literal(0), z.literal(1)]),
        factory_name: z.string().optional(),
      }),
    )
    .max(5000),
  factories: z
    .array(z.object({ entity_id: id, factory_name: z.string(), restricted: z.boolean() }))
    .max(10000),
})
export type ManufacturingOperatorSnapshot = z.infer<typeof manufacturingOperatorSnapshotSchema>
export type ManufacturingFactorySnapshot = z.infer<typeof manufacturingFactorySnapshotSchema>
export type ManufacturingRecord = ManufacturingOperatorSnapshot['requests'][number]
export const manufacturingJobViewSchema = z.object({
  id,
  status: z.enum(['queued', 'processing', 'succeeded', 'failed']),
  error_code: z.string().nullable(),
  sequence: z.number().optional(),
  attempts: z.number().optional(),
  created_at: z.number().optional(),
  completed_at: z.number().nullable().optional(),
})
const envelope = <T extends z.ZodType>(data: T) =>
  z.object({ version: z.number(), updated_at: z.number(), data }).nullable()
export const manufacturingOperatorStateSchema = z.object({
  workspace: envelope(manufacturingOperatorSnapshotSchema),
  jobs: z.array(manufacturingJobViewSchema),
  worker: envelope(z.object({})).optional(),
  bindings: z
    .array(
      z.object({
        clerk_user_id: id,
        local_account_id: id,
        entity_id: id,
        active: z.union([z.boolean(), z.literal(0), z.literal(1)]),
      }),
    )
    .default([]),
})
export const manufacturingFactoryStateSchema = z.object({
  workspace: envelope(manufacturingFactorySnapshotSchema),
  jobs: z.array(manufacturingJobViewSchema).default([]),
})
export type ManufacturingOperatorState = z.infer<typeof manufacturingOperatorStateSchema>
export type ManufacturingFactoryState = z.infer<typeof manufacturingFactoryStateSchema>

export const manufacturingSyncJobSchema = z
  .object({
    id: z.uuid(),
    sequence: z.number().int().positive(),
    lease_token: z.uuid(),
    quote_id: id.nullable(),
    actor_role: z.enum(['customer', 'operator', 'factory', 'sync']),
    actor_id: id,
    factory_account_id: id.nullable(),
    command: manufacturingCommandSchema,
  })
  .strict()
export type ManufacturingSyncJob = z.infer<typeof manufacturingSyncJobSchema>
export const manufacturingSyncAckSchema = z
  .object({
    job_id: z.uuid(),
    lease_token: z.uuid(),
    ok: z.boolean(),
    error_code: z.enum(['QUOTE_CHANGED', 'FORBIDDEN', 'INVALID_COMMAND', 'SYNC_FAILED']).optional(),
    customers: z
      .array(
        z.object({ quote_id: id, subject: id, workspace: manufacturingCustomerSnapshotSchema }),
      )
      .max(2000)
      .default([]),
    factories: z
      .array(z.object({ account_id: id, workspace: manufacturingFactorySnapshotSchema }))
      .max(2000)
      .default([]),
    operator: manufacturingOperatorSnapshotSchema.optional(),
  })
  .strict()
export type ManufacturingSyncAck = z.input<typeof manufacturingSyncAckSchema>
